/* global conditionalblocks */

import { isLegacyConditions, ConditionalBlocksLogo, blankSet } from './utils/helpers.js';
import { initListViewIndicator } from './list-view-indicator.js';

import { ToolbarGroup, ToolbarButton } from '@wordpress/components';
import ConditionalBlocksModal from './components/modal/Modal.js';
import { optionsManager } from './utils/manager.js';
import CategoryIcon from './components/misc/CategoryIcon.js';

const { __ } = wp.i18n;
const { Fragment, useState } = wp.element;
const { InspectorControls } = wp.blockEditor;
const { createHigherOrderComponent } = wp.compose;
const { PanelBody, PanelRow, Button, ExternalLink } = wp.components;
const { BlockControls } = wp.blockEditor;
import { findCategoryFromConditionType, getSingleConditionType } from './utils/registered.js';

/**
 * Add default Attributes for Conditional Blocks to blocks.
 *
 * @param {Object} settings Settings for the block.
 * @return {Object} settings Modified settings.
 */
function addAttributes(settings) {
  // No attributes for the block.
  if (typeof settings.attributes === 'undefined') {
    return settings;
  }

  // Check if block is excluded.
  if (conditionalblocks.excluded_block_types.includes(settings.name)) {
    return settings;
  }

  const conditionsObject = {
    conditionalBlocks: {
      type: 'object',
      default: {
        sets: [], // Contains multiple sets of conditions.
        conditions: [], // Deprecated - conditions are moved into conditions.
      },
    },
  };

  // eslint-disable-next-line no-param-reassign
  settings.attributes = Object.assign(settings.attributes, conditionsObject);

  return settings;
}

wp.hooks.addFilter('blocks.registerBlockType', 'conditionalBlocks/attributes', addAttributes, 1);

/**
 * Get the sets of conditions from the currently selected block.
 *
 * @returns array sets.
 */
const getSets = () => {
  const block = wp.data.select('core/block-editor').getSelectedBlock();

  // CB 3+ requires conditions to be be in a set.
  const uncollectedCondition = block?.attributes?.conditionalBlocks?.conditions?.length
    ? block.attributes.conditionalBlocks.conditions
    : false;

  // Move conditions into a new set and wrap it in an array..
  if (uncollectedCondition) {
    return [blankSet({ conditions: uncollectedCondition })];
  }

  return block?.attributes?.conditionalBlocks?.sets ? [...block.attributes.conditionalBlocks.sets] : [];
};

/**
 * Inject Conditional Blocks onto the blocks.
 *
 * @param {Function} BlockEdit Block edit component.
 *
 * @return {Function} BlockEdit Modified block edit component.
 */
const withAdvancedControls = createHigherOrderComponent((BlockEdit) => (props) => {
  const { name, attributes, isSelected, setAttributes } = props;

  // Create a copy that we can edit.
  const conditionalBlocksAttributes = attributes?.conditionalBlocksAttributes
    ? { ...attributes.conditionalBlocksAttributes }
    : false;

  const [isOpen, setIsOpen] = useState(false);

  // Prevent unnecessary loading.
  if (
    ((!isOpen || !isSelected) && !conditionalblocks.visible_in_editor) ||
    conditionalblocks.excluded_block_types.includes(name)
  ) {
    return (
      <Fragment>
        <BlockEdit {...props} />
      </Fragment>
    );
  }

  const manager = optionsManager();

  const blockHasConditions = getSets().length >= 1;

  const BlockInfo = () => {
    const sets = getSets();

    if (!sets.length) {
      return <Fragment />;
    }

    const hasValidCondition = sets.some((set) => {
      return set.conditions.some((condition) => {
        return condition.type.length;
      });
    });

    if (!hasValidCondition) {
      return <Fragment />;
    }

    return (
      <Fragment>
        <div className="conditional-blocks-builder-modal__row">
          <h5 className="block-editor-block-card__title">
            {__('Active conditions on this block:', 'conditional-blocks')}
          </h5>
        </div>

        {sets.map((set) => {
          const { conditions } = set;

          const output = conditions.map((condition) => {
            if (!condition.type.length) {
              return <Fragment key={condition.id} />;
            }

            const conditionTypeData = getSingleConditionType(condition.type);
            const categoryData = findCategoryFromConditionType(condition.type);

            return (
              <Fragment key={condition.id}>
                <div className="conditional-blocks-builder__active_condition_tag">
                  <CategoryIcon size={14} category={categoryData} />
                  {conditionTypeData?.label ?? 'N/A'}
                </div>
              </Fragment>
            );
          });

          return (
            <div className="conditional-blocks-builder__active_set" key={set.id}>
              {output}
            </div>
          );
        })}
      </Fragment>
    );
  };

  const requiresConversion = !blockHasConditions && isLegacyConditions(conditionalBlocksAttributes);

  /**
   * Deprecated -Save the condition and ensure they are in a set.
   *
   * @param {*} conditions
   */
  const saveConditions = (conditions) => {
    const block = wp.data.select('core/block-editor').getSelectedBlock();

    if (block.clientId) {
      wp.data
        .dispatch('core/block-editor')
        .updateBlockAttributes(block.clientId, { conditionalBlocks: { conditions: [...conditions] } }, false);
    }
  };

  const convertConditions = async () => {
    if (requiresConversion) {
      const converted = await manager.convertLegacyConditions(conditionalBlocksAttributes);

      if (converted) {
        saveConditions(converted);
        // Delete the old attributes.
        setAttributes({ conditionalBlocksAttributes: false });
      }
    }
  };

  return (
    <Fragment>
      <BlockEdit {...props} />
      {isSelected && (
        <InspectorControls group="settings">
          <PanelBody
            title={
              <span>
                <ConditionalBlocksLogo />{' '}
                <span className="conditional-blocks-panel-title">{__('Conditional Blocks', 'conditional-blocks')}</span>
              </span>
            }
            initialOpen={blockHasConditions || requiresConversion}
          >
            <PanelRow>
              <p>
                {__(
                  'Configure visibility conditions to control when this block appears on the site.',
                  'conditional-blocks',
                )}{' '}
                <ExternalLink
                  href={
                    conditionalblocks?.is_pro
                      ? 'https://conditionalblocks.com/docs/?utm_source=conditional-blocks-pro&utm_medium=referral&utm_campaign=modal'
                      : 'https://conditionalblocks.com/docs/?utm_source=conditional-blocks-free&utm_medium=referral&utm_campaign=modal'
                  }
                >
                  Learn more
                </ExternalLink>
              </p>
            </PanelRow>

            {blockHasConditions && (
              <Fragment>
                <BlockInfo />
              </Fragment>
            )}
            <PanelRow>
              {(requiresConversion && (
                <Button isSecondary onClick={convertConditions}>
                  {__('Convert to 2.0 conditions', 'conditional-blocks')}
                </Button>
              )) || (
                  <Button
                    isSecondary
                    onClick={() => {
                      setIsOpen(true);
                    }}
                  >
                    {__('Edit Visibility', 'conditional-blocks')}
                  </Button>
                )}
            </PanelRow>
          </PanelBody>
        </InspectorControls>
      )}
      {isOpen && <ConditionalBlocksModal isOpen={isOpen} setIsOpen={setIsOpen} blockProps={props} />}
    </Fragment>
  );
});

wp.hooks.addFilter('editor.BlockEdit', 'conditionalBlocks/conditionBuilder', withAdvancedControls, 10);

/**
 * Legacy Attributes.
 *
 * @param {Object} settings Settings for the block.
 *
 * @return {Object} settings Modified settings.
 */

function legacyAttributes(settings) {
  // check if object exists for old Gutenberg version compatibility

  if (typeof settings.attributes !== 'undefined') {
    const conditionsObject = {
      conditionalBlocksAttributes: {
        type: 'object',
        default: {
          hideOnMobile: false, // Boolean.
          hideOnTablet: false, // Boolean.
          hideOnDesktop: false, // Boolean.
          userState: '',
          userRoles: [], // array.
          httpReferer: '', // String.
          httpUserAgent: [], // array.
          postMeta: {
            key: '',
            value: '',
            operator: 'true',
          },
          dates: [], // Array.
        },
      },
    };

    // eslint-disable-next-line no-param-reassign
    settings.attributes = Object.assign(settings.attributes, conditionsObject);
  }

  return settings;
}

wp.hooks.addFilter('blocks.registerBlockType', 'conditionalBlocks/custom/attributes', legacyAttributes, 1);

/**
 * Add a class to blocks that have Conditions.
 */
const withCustomClassName = createHigherOrderComponent(
  (BlockListBlock) => (props) => {
    const { attributes } = props;

    const { conditionalBlocks, conditionalBlocksAttributes } = attributes;

    const hasConditions = !!(Array.isArray(conditionalBlocks?.conditions) && conditionalBlocks?.conditions.length >= 1);

    const hasSets = !!(Array.isArray(conditionalBlocks?.sets) && conditionalBlocks?.sets.length >= 1);

    const hasLegacyCondition = isLegacyConditions(conditionalBlocksAttributes);

    if (hasConditions || hasSets || hasLegacyCondition) {
      return <BlockListBlock {...props} className="conditional-blocks-has-conditions" />;
    }
    return <BlockListBlock {...props} />;
  },
  'withClientIdClassName',
);

wp.hooks.addFilter('editor.BlockListBlock', 'conditionalBlocks/editor/blockClasses', withCustomClassName);

const withToolbarButton = createHigherOrderComponent((BlockEdit) => {
  return (props) => {
    const { isSelected, name } = props;
    const [isOpen, setIsOpen] = useState(false);

    const manager = optionsManager();

    // Prevent unnecessary loading.
    if (
      ((!isOpen || !isSelected) && !manager.get('open_from_toolbar')) ||
      !conditionalblocks.visible_in_editor ||
      conditionalblocks.excluded_block_types.includes(name)
    ) {
      return (
        <Fragment>
          <BlockEdit {...props} />
        </Fragment>
      );
    }

    return (
      <Fragment>
        {isSelected && (
          <BlockControls group="block">
            <ToolbarGroup>
              <ToolbarButton
                icon={<ConditionalBlocksLogo className="conditional-blocks-toolbar-button" />}
                label={__('Open Conditional Blocks', 'conditional-blocks')}
                isActive={isOpen}
                onClick={() => {
                  setIsOpen(true);
                }}
              />
            </ToolbarGroup>
          </BlockControls>
        )}
        <BlockEdit {...props} />
        {isOpen && (
          <ConditionalBlocksModal isOpen={isOpen} setIsOpen={setIsOpen} blockProps={props}></ConditionalBlocksModal>
        )}
      </Fragment>
    );
  };
}, 'withToolbarButton');

wp.hooks.addFilter('editor.BlockEdit', 'custom-attributes/with-toolbar-button', withToolbarButton);

// Initialize List View indicator when DOM is ready
wp.domReady(() => {
  initListViewIndicator();
});
